import {
  Component,
  ChangeDetectionStrategy,
  Signal,
  inject,
  effect,
  signal,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit,
  NgZone
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Alert, UserService } from '../services/user.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'alert-dialog',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './alert-dialog.component.html',
  styleUrls: ['./alert-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('appearAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(30px)' }),
        animate('1500ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ])
  ]
})
export class AlertDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  // Ссылки на два видео-плеера для бесшовного цикла
  @ViewChild('frameVideo1') v1!: ElementRef<HTMLVideoElement>;
  @ViewChild('frameVideo2') v2!: ElementRef<HTMLVideoElement>;

  private readonly userService = inject(UserService);
  private readonly zone = inject(NgZone);
  
  public readonly alert: Signal<Alert | null> = this.userService.getAlert();
  public showAlert = signal<boolean>(false);

  private animationId: number = 0;
  private alertTimeout: any;

  constructor() {
    // Следим за появлением нового алерта
    effect(() => {
      const currentAlert = this.alert();
      if (currentAlert?.message?.username) {
        this.zone.run(() => {
          this.showAlert.set(true);
          
          // Очищаем старый таймер, если пришел новый донат раньше времени
          if (this.alertTimeout) clearTimeout(this.alertTimeout);

          this.alertTimeout = setTimeout(() => {
            this.showAlert.set(false);
          }, 55000);
        });
      }
    });
  }

  ngOnInit(): void {
    this.userService.startAlertsPolling();
  }

  ngAfterViewInit(): void {
    // Инициализируем видео-цикл вне зоны Angular для экономии ресурсов
    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        this.initSeamlessLoop(this.v1?.nativeElement, this.v2?.nativeElement);
      }, 300);
    });
  }

  /**
   * Логика бесшовного переключения между двумя видео-файлами
   */
  private initSeamlessLoop(v1: HTMLVideoElement, v2: HTMLVideoElement) {
    if (!v1 || !v2) return;

    const overlapTime = 1.5; // За сколько секунд начинать подготовку следующего видео
    const crossfadeTime = 0.5; // Длительность CSS перехода
    let isPreparingNext = false;

    const setupVideo = (v: HTMLVideoElement) => {
      v.muted = true;
      v.loop = false; // Важно: управляем циклом вручную
      v.preload = 'auto';
    };

    setupVideo(v1);
    setupVideo(v2);

    const checkProgress = () => {
      const current = v1.classList.contains('active') ? v1 : v2;
      const next = current === v1 ? v2 : v1;

      // 1. Предзапуск (Pre-roll): запускаем второе видео заранее
      if (current.currentTime > current.duration - overlapTime && !isPreparingNext) {
        isPreparingNext = true;
        next.currentTime = 0;
        next.play().catch(err => {
          console.warn("Alert video pre-roll failed:", err);
          isPreparingNext = false;
        });
      }

      // 2. Смена активного плеера (Swap)
      if (current.currentTime > current.duration - crossfadeTime && isPreparingNext && !next.classList.contains('active')) {
        next.classList.add('active');
        current.classList.remove('active');

        // Паузим старый плеер только после того, как он полностью исчезнет
        setTimeout(() => {
          if (!current.classList.contains('active')) {
            current.pause();
            isPreparingNext = false;
          }
        }, crossfadeTime * 1000);
      }

      this.animationId = requestAnimationFrame(checkProgress);
    };

    // Запуск первого плеера
    v1.classList.add('active');
    v1.play().then(() => {
      checkProgress();
    }).catch(err => console.error("Initial alert video play failed:", err));
  }

  ngOnDestroy(): void {
    this.userService.stopAlertsPolling();
    
    // Остановка цикла анимации
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }

    // Очистка таймера алерта
    if (this.alertTimeout) {
      clearTimeout(this.alertTimeout);
    }

    // Очистка ресурсов видео
    [this.v1, this.v2].forEach(v => {
      if (v?.nativeElement) {
        v.nativeElement.pause();
        v.nativeElement.src = "";
        v.nativeElement.load();
      }
    });
  }
}